import React, { FC } from 'react';
import { createBrowserRouter, useSearchParams } from 'react-router-dom';
import Root from 'src/shared/Root';
import OpenRegistrationPage from '../pages/openRegistration/page';
import ManagerAccountRegistrationPage from '../pages/managerAccountRegistration/page';
import DspCreateManagerAccountPage from '../pages/dspCreateManagerAccount/page';
import DspActivationRequestPage from '../pages/dspActivationRequest/page';
import DspActivationActivatePage from '../pages/dspActivationActivate/page';
import MarketplaceExpansionPage from '../pages/marketplaceExpansion/page';
import AgencyRegistrationPage from '../pages/agencyRegistration/page';
import TermsPage from '../pages/terms/termsPage';
import TermsChangesPage from '../pages/terms/termsChangesPage';
import TermsApprovalPage from '../pages/terms/termsAgreementPage';
import NotFoundPage from '../pages/errorPages/NotFoundPage';
import SauronProvider from '../shared/sauronProvider';
import AccountSettingsPage from 'src/pages/accountSettings/page';
import IneligibilityRegistrationPage from 'src/pages/IneligibilityRegistration/page';
import RegistrationRouter from 'src/routes/RegistrationRouter';
import SaRegistrationRouter from 'src/routes/SaRegistrationRouter';
import AdspRegistrationRouter from 'src/routes/AdspRegistrationRouter';
import GatewayRegistrationRedirect from 'src/routes/GatewayRegistrationRedirect';
import DataPrivacyTermsPage from 'src/pages/terms/dataPrivacyTermsPage';
import AccountCombinationChoosePage from 'src/pages/accountCombination/combineChoose/AccountCombinationChoose';
import CombineAccountsReviewPage from 'src/pages/accountCombination/combineReview/page';
import CombineAccountsDetailsPage from 'src/pages/accountCombination/combineDetails/page';
import CombineAccountsBillingPage from 'src/pages/accountCombination/combineBilling/page';
import CombineLandingPage from 'src/pages/accountCombination/combineLanding/page';
import UpgradeChoosePage from 'src/pages/accountCombination/upgradeChoose/page';
import ManagerAccountCreationPage from 'src/pages/managerAccountCreation/page';

// agency approval registration and marketplace expansion share a path in AUMS, so we must be backwards compatible
const CreateRouteMiddleware: FC = () => {
  const [searchParams] = useSearchParams();
  const selectedAccountForMarketplaceExpansion = searchParams.get(
    'selectedAccountForMarketplaceExpansion',
  );
  if (selectedAccountForMarketplaceExpansion) {
    return <MarketplaceExpansionPage />;
  }
  // This should return Agency Approval Registration page
  return <AgencyRegistrationPage />;
};

export const router = createBrowserRouter([
  {
    // This route will replace the SauronProvider route.
    // As some pages are still using the earlier implementation, the SauronProvider will not be removed yet.
    path: '/',
    element: <Root />,
    children: [
      {
        path: '/gateway',
        element: <GatewayRegistrationRedirect />,
      },
      {
        path: '/gateway/sso/register',
        element: <GatewayRegistrationRedirect />,
      },
      {
        path: '/registration/kdp/gateway',
        element: <GatewayRegistrationRedirect />,
      },
      {
        path: 'ads-account',
        children: [
          {
            path: 'registration',
            children: [
              {
                path: '',
                element: <RegistrationRouter />,
              },
              // Per-flow sibling routes: the flow is encoded in the path so the Mons gateway can
              // attach a different challenge policy per flow (Policy15 on /sa only). The base path
              // above keeps the adProductGroup query-param switch as a transition fallback.
              {
                path: 'sa',
                element: <SaRegistrationRouter />,
              },
              {
                path: 'adsp',
                element: <AdspRegistrationRouter />,
              },
              {
                path: 'ineligible',
                element: <IneligibilityRegistrationPage />,
              },
            ],
          },
          {
            path: ':entityId',
            children: [
              {
                path: 'account-settings',
                element: <AccountSettingsPage />,
              },
            ],
          },
          {
            path: 'manager-account/create',
            element: <ManagerAccountCreationPage />,
          },
          {
            path: 'combine',
            element: <CombineLandingPage />,
          },
          {
            path: 'combine/choose',
            element: <AccountCombinationChoosePage />,
          },
          {
            path: 'combine/account-details',
            element: <CombineAccountsDetailsPage />,
          },
          {
            path: 'combine/billing',
            element: <CombineAccountsBillingPage />,
          },
          {
            path: 'combine/review',
            element: <CombineAccountsReviewPage />,
          },
          {
            path: 'upgrade/choose',
            element: <UpgradeChoosePage />,
          },
        ],
      },
      {
        path: 'dsp/activate/admin/createManagerAccount',
        element: <DspCreateManagerAccountPage />,
      },
      {
        path: 'dsp/activate/request',
        element: <DspActivationRequestPage />,
      },
      {
        path: 'dsp/activate/admin/activate',
        element: <DspActivationActivatePage />,
      },
    ],
  },
  // These pages can be rendered without a CID, so we will not get sauron context
  {
    path: '/terms',
    element: <TermsPage />,
  },
  {
    path: '/sauron/terms',
    element: <TermsPage />,
  },
  {
    path: '/terms/changes',
    element: <TermsChangesPage />,
  },
  {
    path: '/sauron/terms/changes',
    element: <TermsChangesPage />,
  },
  {
    path: '/terms/data-privacy',
    element: <DataPrivacyTermsPage />,
  },

  // Most pages should belong here since they require Authentication and Sauron
  {
    path: '*',
    element: <SauronProvider />,
    children: [
      {
        path: 'advertisingAccounts/registration/create',
        element: <OpenRegistrationPage />,
      },
      {
        path: 'masterAccounts/create',
        element: <ManagerAccountRegistrationPage />,
      },
      {
        path: 'am/managerAccounts/create',
        element: <ManagerAccountRegistrationPage />,
      },
      {
        path: 'terms/agreement/:token',
        element: <TermsApprovalPage />,
      },
      {
        path: 'advertisingAccounts/create',
        element: <CreateRouteMiddleware />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
